import { ExcelRendered } from '../../../../../components/ExcelField/types'
import { Option } from '../../../../../config/types/component'
import Currency from '../../../../../models/Currency'
import {
    normalize,
    normalizeSymbol,
    toNumber
} from '../../../../../utilities/valueTools'
import { RowError, RowErrorSeverity } from '../hooks/types'

export const HEADER = [
    'codigo_producto',
    'codigo_producto_proveedor',
    'moneda',
    'precio_compra',
    'descripcion_producto_proveedor'
]

const COL_PRODUCT_CODE = 0
const COL_PROVIDER_CODE = 1
const COL_CURRENCY = 2
const COL_PURCHASE_PRICE = 3
const COL_PROVIDER_DESCRIPTION = 4

const statusObj =
    (severity: RowErrorSeverity) =>
    (index: number, field: string, message: string): RowError => ({
        index,
        field,
        message,
        severity
    })

const errorObj = statusObj('error')
const warningObj = statusObj('warning')

export default function preflight(
    excelRendered: ExcelRendered,
    selectedCurrency: Option,
    currencies: Currency[]
): RowError[] {
    const rows = (excelRendered?.rows || []).filter(row => row.length > 0)

    if (rows.length === 0) {
        return [errorObj(null, 'importExcel', 'El archivo está vacío.')]
    }

    const header = rows[0]
    const headerIsValid = HEADER.every(
        (name, i) => normalize(header[i]) === name
    )
    if (!headerIsValid) {
        return [
            errorObj(
                null,
                'importExcel',
                'El formato del Excel no es válido. Use la plantilla que provee el sistema.'
            )
        ]
    }

    const dataRows = rows.slice(1)
    if (dataRows.length === 0) {
        return [
            errorObj(null, 'importExcel', 'El archivo no tiene filas de datos.')
        ]
    }

    return [
        ...validateCurrencyColumn(dataRows, selectedCurrency, currencies),
        ...validateRows(dataRows)
    ]
}

function validateRows(dataRows: Array<(string | number)[]>): RowError[] {
    const errors: RowError[] = []
    const rowByProductCode: Record<string, number> = {}

    dataRows.forEach((row, i) => {
        const rowNumber = i + 1
        const productCode = normalize(row[COL_PRODUCT_CODE])
        const providerCode = normalize(row[COL_PROVIDER_CODE])
        const description = normalize(row[COL_PROVIDER_DESCRIPTION])
        const purchasePrice = toNumber(row[COL_PURCHASE_PRICE])

        if (!productCode) {
            errors.push(
                errorObj(
                    rowNumber,
                    'product_code',
                    'Falta el código de producto.'
                )
            )
        } else if (rowByProductCode[productCode]) {
            errors.push(
                errorObj(
                    rowNumber,
                    'product_code',
                    `Código de producto repetido en el archivo: ${productCode} ya está en la fila ${rowByProductCode[productCode]}.`
                )
            )
        } else {
            rowByProductCode[productCode] = rowNumber
        }

        if (!providerCode) {
            errors.push(
                errorObj(
                    rowNumber,
                    'provider_code',
                    'Falta el código de producto del proveedor.'
                )
            )
        }

        if (!description) {
            errors.push(
                errorObj(
                    rowNumber,
                    'provider_description',
                    'Falta la descripción del producto del proveedor.'
                )
            )
        }

        if (purchasePrice === null) {
            errors.push(
                errorObj(
                    rowNumber,
                    'purchase_price',
                    'El precio de compra tiene que ser un número.'
                )
            )
        } else if (purchasePrice < 0) {
            errors.push(
                errorObj(
                    rowNumber,
                    'purchase_price',
                    'El precio de compra no puede ser negativo.'
                )
            )
        }
    })

    return errors
}

function validateCurrencyColumn(
    dataRows: Array<(string | number)[]>,
    selectedCurrency: Option,
    currencies: Currency[]
): RowError[] {
    if (!selectedCurrency) return []

    const selected = currencies.find(
        currency => currency.id.toString() === selectedCurrency.value
    )
    if (!selected) return []

    const selectedSymbol = normalizeSymbol(selected.symbol)
    const rowsBySymbol: Record<string, number> = {}

    dataRows.forEach(row => {
        const symbol = normalizeSymbol(row[COL_CURRENCY])
        if (!symbol || symbol === selectedSymbol) return
        rowsBySymbol[symbol] = (rowsBySymbol[symbol] || 0) + 1
    })

    return Object.keys(rowsBySymbol).map(symbol => {
        const total = rowsBySymbol[symbol]
        const label = `${total} ${total === 1 ? 'fila' : 'filas'}`
        const known = currencies.find(
            currency => normalizeSymbol(currency.symbol) === symbol
        )
        const message = known
            ? `El archivo indica «${known.symbol} ${known.name}» en ${label}, pero vas a importar en «${selected.symbol} ${selected.name}». Se usará la moneda seleccionada.`
            : `El archivo indica «${symbol}» en ${label}, que no es una moneda habilitada para esta compañía. Se usará «${selected.symbol} ${selected.name}».`

        return warningObj(null, 'currency_id', message)
    })
}
